import { Cart, GPS, nativeAppHeaders } from '@ynap/api-utils'
import {
    CHECKOUT_REQUEST,
    CHECKOUT_SUCCESS,
    CHECKOUT_ERROR,
    CHECKOUT_CLEAR_ERROR,
    FETCH_CART_SUCCESS,
    ERR_ORDER_NOT_READY_CHECKOUT,
} from '../constants/ActionTypes'
import { getCart } from '../selectors'
import { getPaymentConfig, getWatsonConfig, getTimeout } from '@ynap/cart-state/src/selectors/getConfig'
import { getCookie } from '@ynap/redux-cookies-middleware/src/cookieApi'
import getDuties from '../selectors/getDuties'
import getStoreCredit from '../selectors/getStoreCredit'
import { isCvvRequired, getCurrentPaymentOption } from '../selectors/getPaymentStep'
import wrapFetchWithJSON from './common/wrapFetchWithJSON'
import { getFormValues, getFormSyncErrors, isValid, submit, SubmissionError } from 'redux-form'
import { push } from 'connected-react-router'
import { checkRectificationOnError } from './cartRectification'
import { checkGuestIdentityError } from './checkGuestIdentityError'
import { dispatch as dispatchEvent, getTransactionAnalytics } from '@ynap/analytics'
import { suspend, injectAndSubmitForm, injectScript, getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'
import { fingerprintRunner, challengeRunner, collectBrowserInfo, isValidBrowserInfo } from '../../../adyen'
import { getFraudnetFingerprint } from '../../../fraudnet'
import updatePaymentMethod from './updatePaymentMethod'
import { track3DSV2Fallback } from './analytics'
import { notifyBasketChanged, getLocale } from '@ynap/cart-state'
import { CREDIT_CARD, PAYPAL, APPLEPAY } from '../constants/PaymentMethods'
import { formName as dduFormName } from 'app/components/DDUAcceptanceForm/DDUAcceptanceForm'
import { raygun } from '@ynap/clientside-monitoring'
import { setCheckoutNotification } from './checkoutNotification'
import { isOrderReadyCheck } from '../selectors/getOrderReviewStep'
import { ERR_PAYMENT_FAILED } from 'app/components/PaymentOptionsForm/ErrorCodes'
import type { Dispatch } from 'redux'
import type { Address, PaymentOption, ShoppingCart, StoreCredit } from 'app/types/common-types'
import type { CheckoutAnalytics } from 'app/types/analytics.types'
import type { StoreState } from 'app/state/types'
import { Countries } from '@ynap/address-form-v1'

interface State extends StoreState {
    cart: ShoppingCart;
    countries: Countries;
}

type PaymentOptionsForm = {
    billingAddress: Address;
    creditCard: {
        cvv: string;
    }
    paymentMethodId: string;
    returnUrl: string;
}

export function checkoutRequest() {
    return {
        type: CHECKOUT_REQUEST,
    }
}

export function checkoutSuccess(payload) {
    return {
        type: CHECKOUT_SUCCESS,
        payload,
    }
}

export function checkoutError(error) {
    return {
        type: CHECKOUT_ERROR,
        error,
    }
}

export function checkoutClearError() {
    return {
        type: CHECKOUT_CLEAR_ERROR,
    }
}

export function trackPaymentFailed(currentPaymentOption: PaymentOption, errorParameters: string, checkoutAnalytics: CheckoutAnalytics, errorMessage = 'payment', storeCredit: StoreCredit | undefined) {
    // Used to format methods into more readable format for GA. Split / will help with Paypal Billing Agreement (PAYPAL/123)

    const method = currentPaymentOption?.id?.split('/')[0].replace(/_/g, ' ').toLowerCase()

    const transaction = getTransactionAnalytics(checkoutAnalytics)
    const paymentTypeList = [method]
    storeCredit && paymentTypeList.push('store credit')

    const result = {
        event: `${method} - payment failed`,
        effect: 'payment failed within checkout',
        category: {
            primaryCategory: 'checkout',
            subCategory: errorMessage.replace(/_/g, ' ').toLowerCase(),
        },
        transaction: {
            ...transaction,
            attributes: {
                ...transaction?.attributes,
                paymentType: method,
                paymentTypeList,
            },
        },
        ...(errorParameters && {
            attributes: {
                errorParameters,
            },
        }),
    }

    dispatchEvent(result)
}

export function fetchCartSuccess(payload) {
    return {
        type: FETCH_CART_SUCCESS,
        payload,
    }
}

export function trackPaypalRedirectIfNeeded(currentPaymentOption: PaymentOption, checkoutAnalytics: CheckoutAnalytics) {
    if (currentPaymentOption.id === PAYPAL) {
        dispatchEvent({
            event: 'pay with paypal',
            effect: 'open paypal sign in',
            category: {
                primaryCategory: 'checkout',
                subCategory: 'payment',
            },
            transaction: getTransactionAnalytics(checkoutAnalytics),
        })
    }
}

export function submitReduxForm(form: string, dispatch: Dispatch, state: State):PaymentOptionsForm | {} {
    dispatch(submit(form))
    if (!isValid(form)(state)) {
        throw new SubmissionError(getFormSyncErrors(form)(state))
    }
    return getFormValues(form)(state)
}
/* Used for business inteligence team
    e.g.
    native-ios-mrp strategic head/1.1.0
    native-android-mrp strategic head/1.1.0
    web-mrp strategic head/1.1.0
*/
export function getCheckoutAppname(state: State) {
    const deviceKey = state.deviceType ? `native-${state.deviceType}` : 'web'
    const brand = state.config?.head?.brand
    const version = state.config?.head?.version
    return `${deviceKey}-${brand} strategic head/${version}`
}

export function applePayCheckout(returnUrl?: string, forceDDU = false) {
    return async (dispatch: Dispatch, getState: () => State, fetch: WindowOrWorkerGlobalScope['fetch']) => {
        const state = getState()
        const watsonConfig = getWatsonConfig(state)
        const appNameHeaderValue = getCheckoutAppname(state)
        const paymentConfig = getPaymentConfig(state)
        const locale = getLocale(state)
        const accessProfile = 'YNAP_PAYMENT_PSD2'
        const paymentRedirects = {
            x_returnUrl: returnUrl,
            x_cancelUrl: returnUrl,
        }
        const additionalHeaders = {
            'x-origin': window.location.origin,
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }
        const { deliveryDuty } = getDuties(state)
        const currentPaymentOption = getCurrentPaymentOption(state) || {}
        const fetchJSON = wrapFetchWithJSON(fetch, getTimeout(state))

        try {
            let cvv
            if (currentPaymentOption.cardId) {
                if (isCvvRequired(state)) {
                    cvv = ((submitReduxForm('paymentOptions', dispatch, state) || {}) as PaymentOptionsForm).creditCard?.cvv

                    if (paymentConfig.paymentSystem === 'GPS') {
                        await fetchJSON(GPS.setCreditCard({ cardId: currentPaymentOption.cardId, cvsNumber: cvv, ...paymentConfig }))
                    }
                }
            }
            let dduAcceptance = {}
            if (deliveryDuty === 'DDU') {
                if (forceDDU || submitReduxForm(dduFormName, dispatch, state)) {
                    dduAcceptance = { x_dduAcceptance: 'true' }
                }
            }
            let x_beaconSessionId
            if (state.config?.head?.riskified?.enabled) {
                x_beaconSessionId = window?.sessionStorage.getItem('riskifiedSessionId') || undefined
            }
            dispatch(checkoutRequest())
            const json = await fetchJSON(
                Cart.checkout({
                    ...watsonConfig,
                    appNameHeaderValue,
                    accessProfile,
                    langId: locale.langId,
                    body: {
                        ...paymentRedirects,
                        ...dduAcceptance,
                        x_beaconSessionId,
                        x_clientMetaDataId: {},
                    },
                    additionalHeaders,
                })
            )
            if (json.isRedirect || json.redirectParameters) {
                const { redirectMethod, redirectUrl, redirectType, token, paymentSession, ...redirectParams } = json.redirectParameters
                    ? json.redirectParameters[0]
                    : json
                return { redirectType }
            }
            dispatch(checkoutSuccess(json))
            notifyBasketChanged()
        } catch (error) {
            raygun.helpers.agent('send', {
                error: error?.message,
                tags: ['apple pay checkout'],
            })
            return dispatch(checkoutError(error))
        }
    }
}

export default function checkout(returnUrl?: string, forceDDU = false) {
    return async (dispatch: Dispatch, getState: () => State, fetch: WindowOrWorkerGlobalScope['fetch']) => {
        const state = getState()
        const { checkoutAnalytics } = getCart(state)
        const { deliveryDuty } = getDuties(state)
        const watsonConfig = getWatsonConfig(state)
        const paymentConfig = getPaymentConfig(state)
        const currentPaymentOption = getCurrentPaymentOption(state) || {}
        const fetchJSON = wrapFetchWithJSON(fetch, getTimeout(state))
        const locale = getLocale(state)
        const isWebview = !!state.deviceType
        const paypalFingerprintToken = getCookie('paypal_fingerprint_token')
        const { psd2 = {}, fitAnalyticsURL } = state?.config?.head
        const additionalHeaders = {
            'x-origin': window.location.origin,
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }
        const [paymentMethodId, cardId] = currentPaymentOption.id?.split('/') || []
        const isOrderReady = isOrderReadyCheck(state, { messages: state.messages })

        if (paymentMethodId === CREDIT_CARD) {
            //GPS always needs browser info, otherwise will 400
            const isMissingBrowserInfo = !isValidBrowserInfo(currentPaymentOption.browserInfo)
            if (isMissingBrowserInfo) {
                await fetchJSON(
                    Cart.setPaymentInstruction({
                        paymentInstruction: [
                            {
                                payMethodId: paymentMethodId,
                                ...collectBrowserInfo(),
                            },
                        ],
                        clearExisting: 'false',
                        ...watsonConfig,
                        langId: locale.langId,
                        additionalHeaders,
                    })
                )
            }
        }

        let x_clientMetaDataId
        if (paymentMethodId === PAYPAL && currentPaymentOption.billingAgreementToken) {
            if (isWebview && paypalFingerprintToken) {
                x_clientMetaDataId = paypalFingerprintToken
            } else {
                const userEmail = state.cart.x_selfEmailAddress
                const paymentMethods = state.cart.usablePaymentInformation
                const flowId = paymentMethods.filter(
                    (method) => method.billingAgreementToken === currentPaymentOption.billingAgreementToken
                ).billingAgreementFlowId
                x_clientMetaDataId = await getFraudnetFingerprint(flowId, userEmail)
            }
        }

        dispatch(checkoutRequest())
        try {
            let cvv
            // Confirm CVV if required for saved cards
            if (currentPaymentOption.cardId) {
                if (isCvvRequired(state)) {
                    cvv = ((submitReduxForm('paymentOptions', dispatch, state) || {}) as PaymentOptionsForm).creditCard?.cvv

                    if (paymentConfig.paymentSystem === 'GPS') {
                        await fetchJSON(GPS.setCreditCard({ cardId: currentPaymentOption.cardId, cvsNumber: cvv, ...paymentConfig }))
                    }
                }
            }

            let dduAcceptance = {}
            if (deliveryDuty === 'DDU') {
                if (forceDDU || submitReduxForm(dduFormName, dispatch, state)) {
                    dduAcceptance = { x_dduAcceptance: 'true' }
                }
            }

            const appNameHeaderValue = getCheckoutAppname(state)

            let json

            //PSD2 requires new access file
            const accessProfile = 'YNAP_PAYMENT_PSD2'

            const paymentRedirects = {
                x_returnUrl: returnUrl,
                x_cancelUrl: returnUrl,
            }

            let x_beaconSessionId
            if (state.config.head?.riskified?.enabled) {
                x_beaconSessionId = window?.sessionStorage.getItem('riskifiedSessionId') || undefined
            }
            if (!isOrderReady) {
                throw new Error(ERR_ORDER_NOT_READY_CHECKOUT)
            } else {
                json = await fetchJSON(
                    Cart.checkout({
                        ...watsonConfig,
                        appNameHeaderValue,
                        accessProfile,
                        langId: locale.langId,
                        body: {
                            ...paymentRedirects,
                            ...dduAcceptance,
                            x_beaconSessionId,
                            ...(x_clientMetaDataId ? { x_clientMetaDataId } : {}),
                        },
                        additionalHeaders,
                    })
                )
            }

            if (json.isRedirect || json.redirectParameters) {
                const { redirectMethod, redirectUrl, redirectType, token, paymentSession, ...redirectParams } = json.redirectParameters
                    ? json.redirectParameters[0]
                    : json

                trackPaypalRedirectIfNeeded(currentPaymentOption, checkoutAnalytics)

                if (redirectType === 'APPLE_PAY_PAYMENT_SESSION') {
                    window.applePayToken = paymentSession
                    return { redirectType }
                }

                //PSD2 flow
                if (redirectType === 'THREE_DS_2_FINGERPRINT') {
                    try {
                        const fingerprintResult = await fingerprintRunner(token, psd2.sandboxed)
                        const tokenType = 'fingerprint'
                        if (!fingerprintResult) {
                            raygun.helpers.agent('send', { error: '3DS2 fingerprint token not returned', tags: ['checkout', '3ds', 'token', 'fingerprint'] })
                        }

                        return dispatch(updatePaymentMethod({ type: '3ds2', token: fingerprintResult, browserInfo: collectBrowserInfo(), tokenType })).then(
                            () => {
                                return dispatch(checkout())
                            }
                        )
                    } catch (e) {
                        raygun.helpers.agent('send', { error: e?.message, tags: ['checkout', '3ds', 'fingerprint'] })
                    }
                } else if (redirectType === 'THREE_DS_2_CHALLENGE_SHOPPER') {
                    try {
                        const challengeResult = await challengeRunner(token, psd2.sandboxed)
                        const tokenType = 'challenge'
                        if (!challengeResult) {
                            raygun.helpers.agent('send', { error: '3DS2 challenge token not returned', tags: ['checkout', '3ds', 'token', 'challenge'] })
                        }

                        return dispatch(updatePaymentMethod({ type: '3ds2', token: challengeResult, browserInfo: collectBrowserInfo(), tokenType })).then(
                            () => {
                                return dispatch(checkout())
                            }
                        )
                    } catch (e) {
                        // this NEEDS to throw an error, else it will hang on the loading spinner in case of timeout
                        raygun.helpers.agent('send', { error: e?.message, tags: ['checkout', '3ds', 'challenge'] })
                        throw e
                    }
                }
                // Fallback to PSD1 if PSD2 not supported
                else if (redirectType === 'WEB_PAGE_REDIRECT') {
                    dispatchEvent(track3DSV2Fallback(checkoutAnalytics))

                    if (paymentConfig.paymentSystem === 'GPS' && redirectMethod !== 'GET') {
                        injectAndSubmitForm(redirectMethod, redirectUrl, redirectParams)
                    } else {
                        window.location.assign(redirectUrl)
                    }
                } else {
                    injectAndSubmitForm(redirectMethod, redirectUrl, redirectParams)
                }
                return suspend()
            }

            if (json.orderId) {
                injectScript(`https://h.online-metrix.net/fp/tags.js?org_id=qe1251lp&session_id=${json.orderId}`)
            }

            if (fitAnalyticsURL) {
                injectScript(fitAnalyticsURL)
            }

            dispatch(checkoutSuccess(json))
            notifyBasketChanged()
        } catch (error) {
            const { response } = error

            if (error.message === ERR_ORDER_NOT_READY_CHECKOUT) {
                dispatch(checkoutError(error))
                raygun.helpers.agent('send', { error: ERR_ORDER_NOT_READY_CHECKOUT, tags: ['checkout', 'order not ready', 'failed order attempt'] })
                dispatch(push(`/${locale.id}/checkout`))
                dispatch(setCheckoutNotification('error', state.messages.notifications.ERR_ORDER_NOT_READY_CHECKOUT(), { autoHideDelay: 10000, sticky: true }))
            }

            const isDeliveryCutOffError = (errorKey) => {
                return errorKey === 'INVALID_REQUESTED_DATE'
            }

            checkRectificationOnError(error, dispatch)

            let json
            try {
                json = response && (await response.json())
            } catch {
                dispatch(checkoutError(error))
                throw error
            }

            const errors = json?.errors

            const { errorKey, errorParameters = [], errorMessage } = errors?.[0] || {}
            const errorCode = errorParameters[0]?.toLowerCase() === 'gpserrorcode' && errorParameters[1]
            const storeCredit = getStoreCredit(state)

            if (isDeliveryCutOffError(errorKey)) {
                // Refresh the cart state which will have updated shipping options
                dispatch(fetchCartSuccess(json.shoppingCart))
            }

            dispatch(checkoutError(errors ? new Error(errorKey) : error))

            if (errorKey === ERR_PAYMENT_FAILED) {
                trackPaymentFailed(currentPaymentOption, errorCode, checkoutAnalytics, errorMessage, storeCredit)

                dispatch(push(`/${locale.id}/checkout/payment-options`))
            }
            checkGuestIdentityError(error, dispatch, state)

            throw error
        }
    }
}
